import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";
import { TABLET_BREAKPOINT } from "@src/packages/utils/common";
import {
  FileText,
  Image as ImageIcon,
  Paperclip,
  Send,
  X,
} from "lucide-react-native";
import {
  colors,
  Dialog,
  DialogPrimaryButton,
  MoonFieldInput,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  type SupportAttachment,
  type SupportAttachmentDraft,
  type SupportMessage,
  useSupportConversation,
} from "./hooks/useSupportConversation";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

type Props = {
  visible: boolean;
  entryPoint?: "support" | "feedback";
  onClose: () => void;
};

function AttachmentDraftRow({
  attachment,
  onRemove,
}: {
  attachment: SupportAttachmentDraft;
  onRemove: () => void;
}) {
  const capturePress = usePressCapture("AttachmentDraftRow");
  const Icon = attachment.mimeType?.startsWith("image/") ? ImageIcon : FileText;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: semanticColors.border.subtle,
      }}
    >
      <Icon size={15} color={colors.textLight} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={{ flex: 1, color: colors.textMid, fontSize: 12 }}
      >
        {attachment.name}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Remove ${attachment.name}`}
        onPress={() => {
          // X-icon only; its accessibility label is the copy.
          capturePress(pressLabel("Remove attachment"), "remove_attachment");
          onRemove();
        }}
        hitSlop={10}
      >
        <X size={15} color={colors.textFaint} strokeWidth={1.8} />
      </Pressable>
    </View>
  );
}

function isImageAttachment(attachment: SupportAttachment) {
  return attachment.contentType?.startsWith("image/") === true;
}

function ImageAttachmentPreview({
  attachment,
  marginTop,
}: {
  attachment: SupportAttachment;
  marginTop: number;
}) {
  const capturePress = usePressCapture("ImageAttachmentPreview");
  // Chatwoot's thumb_url is a square, center-cropped representation. Use the
  // original data_url for the inline preview so the entire image remains
  // visible, matching Chatwoot's own conversation UI.
  const previewUrl = attachment.url ?? attachment.thumbnailUrl;
  if (!previewUrl) return null;

  const aspectRatio =
    attachment.width && attachment.height
      ? attachment.width / attachment.height
      : 4 / 3;
  const fullSizeUrl = attachment.url;
  const image = (
    <Image
      source={{ uri: previewUrl }}
      accessibilityLabel={attachment.name ?? "Image attachment"}
      resizeMode="contain"
      style={{
        width: "100%",
        aspectRatio,
        maxHeight: 320,
        borderRadius: 10,
        backgroundColor: "rgba(117, 78, 46, 0.06)",
      }}
    />
  );

  return fullSizeUrl ? (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(attachment.name ?? "Open image attachment"),
          "open_image_attachment",
        );
        void Linking.openURL(fullSizeUrl);
      }}
      accessibilityRole="link"
      accessibilityLabel={attachment.name ?? "Open image attachment"}
      style={{ width: "100%", marginTop }}
    >
      {image}
    </Pressable>
  ) : (
    <View style={{ width: "100%", marginTop }}>{image}</View>
  );
}

function MessageBubble({
  message,
  patientLabel,
  teamLabel,
}: {
  message: SupportMessage;
  patientLabel: string;
  teamLabel: string;
}) {
  const capturePress = usePressCapture("SupportMessageBubble");
  const isTeam = message.direction === "team";
  const hasImagePreview = message.attachments.some(
    (attachment) =>
      isImageAttachment(attachment) &&
      Boolean(attachment.thumbnailUrl ?? attachment.url),
  );

  return (
    <View
      style={{
        alignSelf: isTeam ? "flex-start" : "flex-end",
        width: hasImagePreview ? "88%" : undefined,
        maxWidth: "88%",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          alignSelf: isTeam ? "flex-start" : "flex-end",
          color: colors.textFaint,
          fontSize: 10.5,
          fontWeight: "500",
          marginBottom: 4,
          paddingHorizontal: 3,
        }}
      >
        {isTeam ? teamLabel : patientLabel}
      </Text>
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 9,
          borderRadius: 14,
          borderBottomLeftRadius: isTeam ? 4 : 14,
          borderBottomRightRadius: isTeam ? 14 : 4,
          backgroundColor: isTeam
            ? "rgba(255, 255, 255, 0.62)"
            : "rgba(117, 78, 46, 0.10)",
          borderWidth: isTeam ? 1 : 0,
          borderColor: isTeam ? semanticColors.border.subtle : "transparent",
        }}
      >
        {message.content ? (
          <Text
            className="font-dm-sans"
            style={{
              color: colors.text,
              fontSize: 13,
              lineHeight: 18,
            }}
          >
            {message.content}
          </Text>
        ) : null}
        {message.attachments.map((attachment, index) => {
          const marginTop = message.content || index > 0 ? 7 : 0;
          if (
            isImageAttachment(attachment) &&
            (attachment.thumbnailUrl ?? attachment.url)
          ) {
            return (
              <ImageAttachmentPreview
                key={attachment.id}
                attachment={attachment}
                marginTop={marginTop}
              />
            );
          }

          const content = (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginTop,
              }}
            >
              <FileText size={14} color={colors.textLight} strokeWidth={1.8} />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  color: colors.textLight,
                  fontSize: 11.5,
                  textDecorationLine: attachment.url ? "underline" : "none",
                }}
              >
                {attachment.name ?? "Attachment"}
              </Text>
            </View>
          );

          return attachment.url ? (
            <Pressable
              key={attachment.id}
              onPress={() => {
                capturePress(
                  pressLabel(attachment.name ?? "Open attachment"),
                  "open_attachment",
                );
                void Linking.openURL(attachment.url!);
              }}
              accessibilityRole="link"
              accessibilityLabel={attachment.name ?? "Open attachment"}
            >
              {content}
            </Pressable>
          ) : (
            <View key={attachment.id}>{content}</View>
          );
        })}
      </View>
    </View>
  );
}

function ConversationLoadingSkeleton() {
  return (
    <View
      accessibilityLabel="Loading support conversation"
      style={{ gap: 12, paddingVertical: 8 }}
    >
      <View
        style={{
          width: "68%",
          height: 50,
          borderRadius: 14,
          backgroundColor: "rgba(255, 255, 255, 0.44)",
        }}
      />
      <View
        style={{
          alignSelf: "flex-end",
          width: "48%",
          height: 42,
          borderRadius: 14,
          backgroundColor: "rgba(117, 78, 46, 0.08)",
        }}
      />
      <View
        style={{
          width: "57%",
          height: 36,
          borderRadius: 14,
          backgroundColor: "rgba(255, 255, 255, 0.44)",
        }}
      />
    </View>
  );
}

function ConversationEmptyState({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <View
      testID="support-chat-empty-state"
      style={{
        alignItems: "center",
        justifyContent: "center",
        minHeight: 116,
        paddingHorizontal: 20,
        paddingVertical: 18,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{ color: colors.textMid, fontSize: 13, fontWeight: "500" }}
      >
        {title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          color: colors.textFaint,
          fontSize: 12,
          lineHeight: 17,
          marginTop: 5,
          textAlign: "center",
        }}
      >
        {body}
      </Text>
    </View>
  );
}

function ConversationLoadError({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry: () => void;
}) {
  const capturePress = usePressCapture("ConversationLoadError");
  return (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        minHeight: 116,
        paddingHorizontal: 20,
        paddingVertical: 18,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: colors.textLight,
          fontSize: 12.5,
          lineHeight: 18,
          textAlign: "center",
        }}
      >
        {message}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={retryLabel}
        onPress={() => {
          capturePress(pressLabel(retryLabel), "retry_support_conversation");
          onRetry();
        }}
        hitSlop={8}
        style={({ pressed }) => ({ marginTop: 10, opacity: pressed ? 0.6 : 1 })}
      >
        <Text
          className="font-dm-sans"
          style={{ color: colors.warmDark, fontSize: 12.5, fontWeight: "600" }}
        >
          {retryLabel}
        </Text>
      </Pressable>
    </View>
  );
}

type ComposerProps = {
  compact: boolean;
  autoFocus: boolean;
  attachments: SupportAttachmentDraft[];
  attachmentCountText: string;
  canAttachMore: boolean;
  canSend: boolean;
  draft: string;
  inputLabel: string;
  placeholder: string;
  attachLabel: string;
  sendLabel: string;
  isPending: boolean;
  onChangeDraft: (value: string) => void;
  onPickAttachments: () => void;
  onRemoveAttachment: (index: number) => void;
  onSend: () => void;
};

function SupportComposer({
  compact,
  autoFocus,
  attachments,
  attachmentCountText,
  canAttachMore,
  canSend,
  draft,
  inputLabel,
  placeholder,
  attachLabel,
  sendLabel,
  isPending,
  onChangeDraft,
  onPickAttachments,
  onRemoveAttachment,
  onSend,
}: ComposerProps) {
  const capturePress = usePressCapture("SupportComposer");
  return (
    <View style={{ marginTop: compact ? 14 : 16 }}>
      <MoonFieldInput paddingX={12} paddingY={0}>
        <TextInput
          value={draft}
          onChangeText={onChangeDraft}
          placeholder={placeholder}
          placeholderTextColor={colors.textFaint}
          accessibilityLabel={inputLabel}
          autoFocus={autoFocus}
          autoCorrect
          multiline
          maxLength={5_000}
          editable={!isPending}
          className="font-dm-sans"
          style={{
            minHeight: compact ? 48 : 84,
            maxHeight: compact ? 100 : 132,
            paddingTop: compact ? 13 : 11,
            paddingBottom: compact ? 10 : 8,
            color: colors.text,
            fontSize: 14,
            lineHeight: 20,
            textAlignVertical: "top",
          }}
        />
        {attachments.map((attachment, index) => (
          <AttachmentDraftRow
            key={`${attachment.uri}-${index}`}
            attachment={attachment}
            onRemove={() => onRemoveAttachment(index)}
          />
        ))}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: attachments.length ? 7 : compact ? 3 : 2,
            paddingBottom: compact ? 8 : 4,
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={attachLabel}
            onPress={() => {
              capturePress(pressLabel(attachLabel), "pick_support_attachments");
              onPickAttachments();
            }}
            disabled={!canAttachMore || isPending}
            style={({ pressed }) => ({
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              opacity: !canAttachMore || isPending ? 0.45 : pressed ? 0.65 : 1,
              paddingVertical: 5,
              paddingRight: 6,
            })}
          >
            <Paperclip size={15} color={colors.textLight} strokeWidth={1.8} />
            {!compact ? (
              <Text
                className="font-dm-sans"
                style={{ color: colors.textLight, fontSize: 12 }}
              >
                {attachLabel}
              </Text>
            ) : null}
          </Pressable>
          {compact ? (
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
            >
              {attachments.length > 0 ? (
                <Text
                  className="font-dm-sans"
                  style={{ color: colors.textFaint, fontSize: 11 }}
                >
                  {attachmentCountText}
                </Text>
              ) : null}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={sendLabel}
                onPress={() => {
                  // Icon-only send; its accessibility label is the copy.
                  capturePress(pressLabel(sendLabel), "send_support_message");
                  onSend();
                }}
                disabled={!canSend || isPending}
                style={({ pressed }) => ({
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor:
                    canSend && !isPending
                      ? colors.deepBrown
                      : "rgba(74, 63, 53, 0.14)",
                  opacity: pressed && canSend && !isPending ? 0.78 : 1,
                })}
              >
                <Send
                  size={14}
                  color={
                    canSend && !isPending ? colors.surface : colors.textFaint
                  }
                  strokeWidth={2}
                />
              </Pressable>
            </View>
          ) : (
            <Text
              className="font-dm-sans"
              style={{ color: colors.textFaint, fontSize: 11 }}
            >
              {attachmentCountText}
            </Text>
          )}
        </View>
      </MoonFieldInput>

      {!compact ? (
        <DialogPrimaryButton
          label={sendLabel}
          onPress={onSend}
          disabled={!canSend}
          loading={isPending}
          style={{ marginTop: 16 }}
        />
      ) : null}
    </View>
  );
}

/**
 * Louise-owned support UX. Support opens as a conversation (with its durable
 * history), while feedback stays a short direct submission that writes to that
 * same conversation.
 */
export function SupportConversationModal({
  visible,
  entryPoint = "support",
  onClose,
}: Props) {
  const t = useTranslations();
  const isFeedback = entryPoint === "feedback";
  const { width } = useWindowDimensions();
  // A chat thread is too cramped in a centered card on a phone; present the
  // support conversation full-screen on small viewports. Feedback stays a short
  // centered form, and tablets/desktop keep the centered card.
  const fullscreen = !isFeedback && width < TABLET_BREAKPOINT;
  const { conversation, sendMessage } = useSupportConversation(
    visible && !isFeedback,
  );
  const messageScrollRef = useRef<ScrollView>(null);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<SupportAttachmentDraft[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const canSend = Boolean(draft.trim() || attachments.length > 0);
  const messageError =
    localError ??
    (sendMessage.error instanceof Error ? sendMessage.error.message : null);
  const messages = conversation.data?.messages ?? [];
  const title = isFeedback
    ? t("you.support.message_feedback_title")
    : t("you.support.chat_title");
  const subtitle = isFeedback
    ? t("you.support.message_feedback_subtitle")
    : t("you.support.chat_subtitle");

  const canAttachMore = attachments.length < MAX_ATTACHMENTS;
  const attachmentCountText = useMemo(
    () => `${attachments.length}/${MAX_ATTACHMENTS}`,
    [attachments.length],
  );

  useEffect(() => {
    if (visible) return;
    setDraft("");
    setAttachments([]);
    setLocalError(null);
    setFeedbackSent(false);
  }, [visible]);

  useEffect(() => {
    if (isFeedback || messages.length === 0) return;
    const timeout = setTimeout(() => {
      messageScrollRef.current?.scrollToEnd({ animated: false });
    }, 0);
    return () => clearTimeout(timeout);
  }, [isFeedback, messages.length]);

  const pickAttachments = async () => {
    if (!canAttachMore || sendMessage.isPending) return;
    setLocalError(null);
    const result = await DocumentPicker.getDocumentAsync({
      type: "*/*",
      multiple: true,
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets?.length) return;

    const oversized = result.assets.find(
      (asset) => (asset.size ?? 0) > MAX_ATTACHMENT_BYTES,
    );
    if (oversized) {
      setLocalError(t("you.support.message_attachment_too_large"));
      return;
    }

    const room = MAX_ATTACHMENTS - attachments.length;
    const selected = result.assets.slice(0, room).map((asset) => ({
      uri: asset.uri,
      name: asset.name ?? "Attachment",
      mimeType: asset.mimeType,
      size: asset.size,
      file: asset.file,
    }));
    setAttachments((current) => [...current, ...selected]);
    if (result.assets.length > room) {
      setLocalError(t("you.support.message_too_many_attachments"));
    }
  };

  const handleSend = async () => {
    if (!canSend || sendMessage.isPending) return;
    setLocalError(null);
    try {
      await sendMessage.mutateAsync({
        content: draft,
        entryPoint,
        attachments,
      });
      setDraft("");
      setAttachments([]);
      if (isFeedback) setFeedbackSent(true);
    } catch {
      // The mutation error is intentionally surfaced inline below the
      // composer. Keeping the draft intact makes retrying one tap.
    }
  };

  const composer = (
    <SupportComposer
      compact={!isFeedback}
      autoFocus={isFeedback && visible}
      attachments={attachments}
      attachmentCountText={attachmentCountText}
      canAttachMore={canAttachMore}
      canSend={canSend}
      draft={draft}
      inputLabel={t("you.support.message_input_label")}
      placeholder={t("you.support.message_placeholder")}
      attachLabel={t("you.support.message_attach")}
      sendLabel={t("you.support.message_send")}
      isPending={sendMessage.isPending}
      onChangeDraft={(value) => {
        setDraft(value);
        if (localError) setLocalError(null);
        if (feedbackSent) setFeedbackSent(false);
      }}
      onPickAttachments={pickAttachments}
      onRemoveAttachment={(index) =>
        setAttachments((current) =>
          current.filter((_, itemIndex) => itemIndex !== index),
        )
      }
      onSend={handleSend}
    />
  );

  const content = (
    <>
      <Text
        accessibilityRole="header"
        className="font-dm-sans"
        style={{
          color: colors.text,
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          paddingRight: 28,
        }}
      >
        {title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          color: colors.textLight,
          fontSize: 13,
          lineHeight: 18,
          marginTop: 5,
        }}
      >
        {subtitle}
      </Text>

      {isFeedback ? (
        <>
          {composer}
          {feedbackSent ? (
            <Text
              className="font-dm-sans"
              style={{
                color: colors.textLight,
                fontSize: 12,
                lineHeight: 17,
                marginTop: 10,
              }}
            >
              {t("you.support.message_feedback_sent")}
            </Text>
          ) : null}
        </>
      ) : (
        <>
          <View
            accessibilityLabel={t("you.support.chat_history_label")}
            style={
              fullscreen
                ? { flex: 1, marginTop: 16 }
                : {
                    maxHeight: 236,
                    minHeight: conversation.isPending ? 132 : undefined,
                    marginTop: 16,
                  }
            }
          >
            {conversation.isPending ? (
              <ConversationLoadingSkeleton />
            ) : conversation.isError && messages.length === 0 ? (
              <ConversationLoadError
                message={t("you.support.chat_load_error")}
                retryLabel={t("you.support.chat_retry")}
                onRetry={() => void conversation.refetch()}
              />
            ) : messages.length > 0 ? (
              <ScrollView
                ref={messageScrollRef}
                style={fullscreen ? { flex: 1 } : { maxHeight: 236 }}
                contentContainerStyle={{ gap: 12, paddingRight: 2 }}
                showsVerticalScrollIndicator={false}
                keyboardDismissMode="on-drag"
                keyboardShouldPersistTaps="handled"
                onContentSizeChange={() =>
                  messageScrollRef.current?.scrollToEnd({ animated: false })
                }
              >
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    patientLabel={t("you.support.message_from_you")}
                    teamLabel={t("you.support.message_from_support")}
                  />
                ))}
              </ScrollView>
            ) : (
              <ConversationEmptyState
                title={t("you.support.chat_empty_title")}
                body={t("you.support.chat_empty_subtitle")}
              />
            )}
          </View>

          {composer}
        </>
      )}

      {messageError ? (
        <Text
          className="font-dm-sans"
          style={{
            color: colors.coral,
            fontSize: 12,
            lineHeight: 16,
            marginTop: 8,
          }}
        >
          {messageError}
        </Text>
      ) : null}
    </>
  );

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={fullscreen ? "fullscreen" : "centered"}
      closeButton="minimal"
      contentStyle={
        fullscreen
          ? undefined
          : { paddingTop: 26, paddingHorizontal: 24, paddingBottom: 22 }
      }
      testID="support-conversation-modal"
    >
      {fullscreen ? (
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
        >
          {content}
        </KeyboardAvoidingView>
      ) : (
        content
      )}
    </Dialog>
  );
}

export default SupportConversationModal;
